import UsuarioDataService from "@/services/acessos/UsuarioDataService";

function base64UrlToArrayBuffer(value) {
  const base64 = String(value || "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );
  const binary = window.atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes.buffer;
}

function arrayBufferToBase64Url(buffer) {
  if (!buffer) {
    return null;
  }
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window
    .btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
}

function decodeCreationOptions(publicKey) {
  return {
    ...(publicKey || {}),
    challenge: base64UrlToArrayBuffer(publicKey?.challenge),
    user: {
      ...(publicKey?.user || {}),
      id: base64UrlToArrayBuffer(publicKey?.user?.id),
    },
    excludeCredentials: (publicKey?.excludeCredentials || []).map(
      (credential) => ({
        ...credential,
        id: base64UrlToArrayBuffer(credential.id),
      })
    ),
  };
}

function decodeRequestOptions(publicKey) {
  return {
    ...(publicKey || {}),
    challenge: base64UrlToArrayBuffer(publicKey?.challenge),
    allowCredentials: (publicKey?.allowCredentials || []).map((credential) => ({
      ...credential,
      id: base64UrlToArrayBuffer(credential.id),
    })),
  };
}

function encodeRegistrationCredential(credential) {
  return {
    id: credential.id,
    type: credential.type,
    rawId: arrayBufferToBase64Url(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    response: {
      clientDataJSON: arrayBufferToBase64Url(
        credential.response.clientDataJSON
      ),
      attestationObject: arrayBufferToBase64Url(
        credential.response.attestationObject
      ),
      transports:
        typeof credential.response.getTransports === "function"
          ? credential.response.getTransports()
          : undefined,
    },
    clientExtensionResults:
      typeof credential.getClientExtensionResults === "function"
        ? credential.getClientExtensionResults()
        : {},
  };
}

function encodeAssertionCredential(credential) {
  return {
    id: credential.id,
    type: credential.type,
    rawId: arrayBufferToBase64Url(credential.rawId),
    authenticatorAttachment: credential.authenticatorAttachment || undefined,
    response: {
      clientDataJSON: arrayBufferToBase64Url(
        credential.response.clientDataJSON
      ),
      authenticatorData: arrayBufferToBase64Url(
        credential.response.authenticatorData
      ),
      signature: arrayBufferToBase64Url(credential.response.signature),
      userHandle: arrayBufferToBase64Url(credential.response.userHandle),
    },
    clientExtensionResults:
      typeof credential.getClientExtensionResults === "function"
        ? credential.getClientExtensionResults()
        : {},
  };
}

export async function isPasskeyAvailable() {
  if (
    typeof window === "undefined" ||
    !window.PublicKeyCredential ||
    !navigator.credentials
  ) {
    return false;
  }
  if (
    typeof window.PublicKeyCredential
      .isUserVerifyingPlatformAuthenticatorAvailable !== "function"
  ) {
    return true;
  }
  return window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable().catch(
    () => false
  );
}

export async function authenticateWithPasskey(passkeyOptions) {
  if (!passkeyOptions?.requestId || !passkeyOptions?.publicKey) {
    return null;
  }
  if (!(await isPasskeyAvailable())) {
    return null;
  }
  const credential = await navigator.credentials.get({
    publicKey: decodeRequestOptions(passkeyOptions.publicKey),
  });
  if (!credential) {
    return null;
  }
  return {
    requestId: passkeyOptions.requestId,
    credential: encodeAssertionCredential(credential),
  };
}

export async function registerPasskey(descricao) {
  if (!(await isPasskeyAvailable())) {
    return null;
  }
  const start = await UsuarioDataService.iniciarRegistroPasskey({
    descricaoDispositivoAutenticacaoDoisFatores: String(descricao || "")
      .trim()
      .slice(0, 120),
  });
  const credential = await navigator.credentials.create({
    publicKey: decodeCreationOptions(start.data.publicKey),
  });
  if (!credential) {
    return null;
  }
  const finish = await UsuarioDataService.finalizarRegistroPasskey({
    requestId: start.data.requestId,
    credential: encodeRegistrationCredential(credential),
  });
  return finish.data;
}
